import { Chart } from 'chart.js';
import { Plugin } from 'chart.js/auto';

import { Counts, distributionAverage } from '../../../utils/functions/stats';

export type MarkerPluginType = Plugin<'bar'> & {
  beforeDatasetsDraw?: (chart: Chart<'bar'>) => void;
};

export type MarkerFont = {
  size?: number;
  weight?: string;
  family?: string;
};

const drawMarker = (
  ctx: CanvasRenderingContext2D,
  xValue: number,
  top: number
) => {
  const lineWidth = 10;
  const lineX = xValue - lineWidth / 2;
  const lineY = top - 20;
  ctx.strokeStyle = 'grey';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(lineX, lineY);
  ctx.lineTo(lineX, lineY + lineWidth);
  ctx.stroke();
};

const addTextWithTooltip = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  xValue: number,
  top: number,
  avgType: 'CURRENT' | 'PREVIOUS',
  text: string,
  prevAvgDescription: string | null,
  font: MarkerFont
) => {
  const label = avgType === 'CURRENT' ? 'Avg' : 'Prev. avg';
  const displayText = `${label}: ${text}`;

  const fontSize = font.size ?? 11;
  const fontWeight = font.weight ?? 'bold';
  const fontFamily = font.family ?? 'sans-serif';

  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  ctx.fillStyle = 'grey';

  // Define text position - center the text horizontally over the xValue
  const textWidth = ctx.measureText(displayText).width;
  const textX = xValue - textWidth / 2;
  const textY = top - (avgType === 'CURRENT' ? 30 : 46);

  ctx.fillText(displayText, textX, textY, 1000);

  // Function to check if mouse is over text
  const isMouseOverText = (event: MouseEvent) => {
    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    return (
      mouseX >= textX &&
      mouseX <= textX + ctx.measureText(displayText).width &&
      mouseY >= textY - 12 &&
      mouseY <= textY + 4
    );
  };

  // Event listeners to show tooltip on hover
  canvas.addEventListener('mousemove', (event) => {
    const tooltipElement = document.getElementById('tooltip');
    if (
      avgType !== 'CURRENT' &&
      isMouseOverText(event) &&
      tooltipElement?.style.display !== 'block'
    ) {
      // Show tooltip
      showTooltip(text, event.clientX, event.clientY, prevAvgDescription);
    }
  });

  canvas.addEventListener('mouseleave', () => {
    // Hide tooltip when mouse leaves text area
    hideTooltip();
  });
};

// Function to show tooltip
const showTooltip = (
  text: string,
  x: number,
  y: number,
  prevAvgDescription: string | null
) => {
  let tooltipElement = document.getElementById('tooltip');
  const survey = String(prevAvgDescription)?.split('\n')[0]?.split(':')[1];
  const date = new Date(
    String(prevAvgDescription)?.split('\n')[1]?.split(':')[1]
  ).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
  });

  if (!tooltipElement) {
    // Create tooltip container
    tooltipElement = document.createElement('div');
    tooltipElement.id = 'tooltip';
    tooltipElement.style.position = 'fixed';
    tooltipElement.style.display = 'none';
    tooltipElement.style.backgroundColor = '#3C3A3AD9';
    tooltipElement.style.font = '12px sans-serif';
    tooltipElement.style.color = 'white';
    tooltipElement.style.padding = '8px';
    tooltipElement.style.borderRadius = '4px';
    tooltipElement.style.zIndex = '9999';
    const htmlElement = document.createElement('div');
    htmlElement.innerHTML = `<p>
      Previous average: ${text}
      <br />
      ${survey ? 'Survey: ' + survey : ''}
      <br />
      ${date ? 'Date: ' + date : ''}
    </p>`;
    tooltipElement.appendChild(htmlElement);

    document.body.appendChild(tooltipElement);
  }
  tooltipElement.style.display = 'block';
  tooltipElement.style.top = `${y}px`;
  tooltipElement.style.left = `${x}px`;
};

// Function to hide tooltip
const hideTooltip = () => {
  const tooltipElement = document.getElementById('tooltip');
  if (tooltipElement) {
    tooltipElement.style.display = 'none';
  }
};

const createMarkerPlugin = (
  avgType: 'CURRENT' | 'PREVIOUS',
  value: number,
  prevAvgDescription: string | null,
  rangeValue?: number,
  font: MarkerFont = {}
): MarkerPluginType => {
  const label = avgType === 'CURRENT' ? 'Avg' : 'Prev. avg';
  return {
    id: label,
    beforeDatasetsDraw(chart: Chart<'bar'>) {
      const {
        canvas,
        ctx,
        chartArea: { top },
        scales: { x },
      } = chart;
      ctx.save();

      drawMarker(
        ctx,
        x.getPixelForValue(rangeValue ? value / rangeValue - 0.5 : value),
        top
      );
      addTextWithTooltip(
        canvas,
        ctx,
        x.getPixelForValue(rangeValue ? value / rangeValue - 0.5 : value),
        top,
        avgType,
        value.toFixed(2),
        prevAvgDescription,
        font
      );

      ctx.restore();
    },
  };
};

export const prevAverageMarkerPlugin = (
  prevAvgValue: string | number | boolean,
  prevAvgDescription: string,
  rangeValue?: number,
  font: MarkerFont = {}
): MarkerPluginType => {
  const parsedPrevAvgValue = () => {
    if (typeof prevAvgValue === 'string') return parseFloat(prevAvgValue);
    else if (typeof prevAvgValue === 'boolean') return undefined;
    else return prevAvgValue;
  };
  const prevAvg = parsedPrevAvgValue();

  if (prevAvg === undefined || isNaN(prevAvg) || prevAvg === 0) {
    // don't draw average marker if there is no avgValue
    return { id: 'prevAverageMarker' };
  }
  return createMarkerPlugin(
    'PREVIOUS',
    prevAvg,
    prevAvgDescription,
    rangeValue,
    font
  );
};

export const averageMarkerPlugin = (
  counts: Counts,
  rangeValue?: number,
  font: MarkerFont = {}
): MarkerPluginType => {
  const totalAnswered = Object.values(counts).reduce((a, b) => a + b, 0);
  if (totalAnswered === 0) {
    // don't draw average marker if there are no answers
    return { id: 'averageMarker' };
  }
  const avgValue = distributionAverage(counts);
  return createMarkerPlugin('CURRENT', avgValue, null, rangeValue, font);
};
